/**
 * Which rows changed since the last time this list was rendered.
 *
 * The queue refetches on a timer, so rows move and change under somebody who
 * is reading them. A change that happens silently is a change nobody sees —
 * and the one after it is the one they act on, wrongly.
 *
 * THREE THINGS IT MUST NOT DO, and each is a way this feature becomes
 * irritating rather than useful:
 *
 *   - flash on FIRST LOAD. Every row is new to an empty list, and a queue
 *     that lights up entirely on arrival has told you nothing.
 *   - flash on a FILTER OR SORT CHANGE. Switching to "Mine" replaces the
 *     whole list; those rows are not new events, they are a different
 *     question. The scope key below is what tells those apart.
 *   - flash on REORDER. A row that moved because something else changed has
 *     not itself changed, and highlighting it points at the wrong ticket.
 *
 * So: a change is a row whose own `updated_at` moved, or a row that appeared
 * in a list that was already being watched at the same scope.
 */
import { useEffect, useRef, useState } from 'react';

export interface ChangeableRow {
  id: string;
  updated_at: string;
}

/**
 * How long a row stays marked.
 *
 * Long enough to catch the eye of somebody already looking at the screen,
 * short enough that a row changing twice in a minute does not leave a
 * permanent tint. It is an event, not a state.
 */
export const CHANGE_MARK_MS = 2400;

export function useChangedRows<T extends ChangeableRow>(
  rows: readonly T[],
  /**
   * What question this list is answering — the query key, the filters, the
   * sort. When it changes, the baseline is replaced WITHOUT marking
   * anything: a different question has different answers, and none of them
   * are events.
   */
  scope: string,
): Set<string> {
  const [changed, setChanged] = useState<Set<string>>(() => new Set());
  const seen = useRef<Map<string, string> | null>(null);
  const seenScope = useRef<string | null>(null);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    /**
     * AN EMPTY LIST IS NOT A BASELINE.
     *
     * This is the whole of a reported bug: every row in the queue flashed
     * on page load and on refresh. The first render happens before the
     * query resolves, so `rows` is `[]` -- and that empty list was taken as
     * the baseline. The first real page then compared against nothing,
     * found every id absent, and marked all eighty rows as new arrivals.
     *
     * Returning early also covers a filter that momentarily matches
     * nothing: the baseline from before it emptied is kept, so rows coming
     * back are recognised rather than greeted.
     */
    if (rows.length === 0) return;

    const next = new Map(rows.map((r) => [r.id, r.updated_at]));

    // First sight of this list, or a different question. Take the baseline
    // and mark nothing.
    if (seen.current === null || seenScope.current !== scope) {
      seen.current = next;
      seenScope.current = scope;
      return;
    }

    /**
     * THROUGH THE PURE FUNCTION, not a copy of it.
     *
     * This loop used to be written out again here, and the copy is how the
     * bug above survived a test suite: `rowsThatChanged` was tested
     * thoroughly and was never the code that ran. Two implementations of
     * one rule means the tested one and the shipped one can disagree, and
     * they did.
     */
    const previous = seen.current;
    const fresh = rowsThatChanged(previous, rows);
    seen.current = next;
    if (fresh.length === 0) return;

    setChanged((cur) => {
      const merged = new Set(cur);
      for (const id of fresh) merged.add(id);
      return merged;
    });

    for (const id of fresh) {
      // Restart the clock for a row that changed again while still marked,
      // rather than letting the first timer clear a newer change.
      const running = timers.current.get(id);
      if (running) clearTimeout(running);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          setChanged((cur) => {
            if (!cur.has(id)) return cur;
            const without = new Set(cur);
            without.delete(id);
            return without;
          });
        }, CHANGE_MARK_MS),
      );
    }
  }, [rows, scope]);

  useEffect(() => {
    const running = timers.current;
    return () => {
      for (const t of running.values()) clearTimeout(t);
      running.clear();
    };
  }, []);

  return changed;
}

/**
 * The pure half, exported so the rules above can be tested without a DOM.
 *
 * Returns the ids that should be marked, given what was on screen before.
 * `null` for `previous` means "no baseline" — first load or a new scope —
 * and marks nothing, which is the whole point.
 */
export function rowsThatChanged<T extends ChangeableRow>(
  previous: Map<string, string> | null,
  rows: readonly T[],
): string[] {
  /**
   * No baseline, or an EMPTY one, marks nothing.
   *
   * `null` is "we have never seen this list". An empty map is the same
   * claim wearing different clothes -- it is what a list looks like before
   * its query resolves -- and treating it as evidence that every row is new
   * is what lit up the whole queue on every page load.
   */
  if (previous === null || previous.size === 0) return [];
  const out: string[] = [];
  for (const r of rows) {
    const before = previous.get(r.id);
    if (before === undefined || before !== r.updated_at) out.push(r.id);
  }
  return out;
}
